import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Brain, Loader2, Check, Send, Clock, FileText, Table, Download, BookmarkPlus, Bell, ChevronRight, Search, PenLine, ChevronDown, Paperclip, Mic } from 'lucide-react';
import { scaleDelay } from '../../hooks/demoSpeed';

const ARTIFACT_ICONS = { pdf: FileText, spreadsheet: Table };

// Render markdown-lite: **bold** → <strong>, preserve line breaks
const RichText = ({ text, className }) => {
  const lines = text.split('\n');
  return (
    <div className={className}>
      {lines.map((line, i) => {
        if (line.trim() === '') return <div key={i} style={{ height: '12px' }} />;
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        return (
          <p key={i}>
            {parts.map((part, j) => {
              if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={j} className="font-semibold">{part.slice(2, -2)}</strong>;
              }
              return <span key={j}>{part}</span>;
            })}
          </p>
        );
      })}
    </div>
  );
};

const AttachmentChip = ({ a, isDark, compact }) => {
  const Icon = ARTIFACT_ICONS[a.type] || FileText;
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isDark ? 'bg-white/[0.04] border border-white/[0.06]' : 'bg-black/[0.03] border border-black/[0.05]'} ${compact ? 'flex-1 min-w-0' : ''}`}>
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${textSecondary}`} />
      <span className={`text-[11px] font-medium truncate ${textPrimary}`}>{a.name}</span>
      <span className={`text-[10px] flex-shrink-0 ${textSecondary}`}>{a.size}</span>
      <Download className={`w-3 h-3 flex-shrink-0 ml-auto ${textSecondary} cursor-pointer`} />
    </div>
  );
};

const V4EmailReply = ({ isDark, emailId, emailData, onBack, onEmailHandled, pendingEmails, allReplies }) => {
  const reply = allReplies[emailId];
  const [emailShown, setEmailShown] = useState(false);
  const [fullEmailOpen, setFullEmailOpen] = useState(false);
  const [thinkingStep, setThinkingStep] = useState(-1);
  const [thinkingDone, setThinkingDone] = useState(false);
  const [thinkingSeconds, setThinkingSeconds] = useState(0);
  const [introVisible, setIntroVisible] = useState(false);
  const [draftVisible, setDraftVisible] = useState(false);
  const [promptVisible, setPromptVisible] = useState(false);
  const [actionTaken, setActionTaken] = useState(null);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [reminderConvo, setReminderConvo] = useState([]);
  const [showNextPrompt, setShowNextPrompt] = useState(false);
  // Edit flow state
  const [editMessages, setEditMessages] = useState([]);
  const [editDraftVisible, setEditDraftVisible] = useState(false);
  const [editThinking, setEditThinking] = useState(false);
  const [editDone, setEditDone] = useState(false);
  const [editPromptVisible, setEditPromptVisible] = useState(false);
  const timerRefs = useRef([]);
  const scrollRef = useRef(null);

  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const divider = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';
  const solidBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';
  const agentText = `text-[14px] leading-[1.8] ${isDark ? 'text-white/85' : 'text-slate-700'}`;
  const userBubble = `px-5 py-3.5 rounded-2xl rounded-br-md text-[14px] leading-[1.8] ${isDark ? 'bg-indigo-500/20 text-white' : 'bg-indigo-100 border border-indigo-200/80 text-slate-800'}`;

  const addTimer = (cb, delay) => { const id = setTimeout(cb, scaleDelay(delay)); timerRefs.current.push(id); };

  useEffect(() => {
    timerRefs.current.forEach(clearTimeout);
    timerRefs.current = [];
    setEmailShown(false); setFullEmailOpen(false); setThinkingStep(-1);
    setThinkingDone(false); setThinkingSeconds(0);
    setIntroVisible(false); setDraftVisible(false); setPromptVisible(false);
    setActionTaken(null); setConfirmVisible(false); setReminderConvo([]); setShowNextPrompt(false);
    setEditMessages([]); setEditDraftVisible(false); setEditThinking(false); setEditDone(false); setEditPromptVisible(false);

    addTimer(() => setEmailShown(true), 400);
    const steps = reply?.thinkingSteps || [];
    const thinkingStart = 2800;
    let delay = thinkingStart;
    steps.forEach((_, i) => { addTimer(() => setThinkingStep(i), delay); delay += 2200; });
    const thinkingEnd = delay;
    const totalThinkSec = reply?.thinkingTime || Math.round((thinkingEnd - thinkingStart) / 1000);
    addTimer(() => { setThinkingStep(-1); setThinkingSeconds(totalThinkSec); setThinkingDone(true); }, delay);
    delay += 1200;
    addTimer(() => setIntroVisible(true), delay);
    delay += 2500;
    addTimer(() => setDraftVisible(true), delay);
    delay += 1200;
    addTimer(() => setPromptVisible(true), delay);
    return () => timerRefs.current.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailId]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [emailShown, thinkingStep, thinkingDone, introVisible, draftVisible, promptVisible, actionTaken, confirmVisible, reminderConvo, showNextPrompt, editMessages, editDraftVisible, editThinking, editPromptVisible]);

  const remaining = pendingEmails.filter(e => e.id !== emailId).length;
  const handleSend = () => { setActionTaken('sent'); setPromptVisible(false); setEditPromptVisible(false); onEmailHandled?.(emailId, 'sent'); addTimer(() => { setConfirmVisible(true); setShowNextPrompt(true); }, 800); };
  const handleDraft = () => { setActionTaken('drafted'); setPromptVisible(false); setEditPromptVisible(false); onEmailHandled?.(emailId, 'drafted'); addTimer(() => { setConfirmVisible(true); setShowNextPrompt(true); }, 800); };
  const handleRemindLater = () => { setActionTaken('reminding'); setPromptVisible(false); setEditPromptVisible(false); setReminderConvo([{ role: 'agent', text: 'Sure thing — when should I bring this back? I can nudge you at a specific time today, or push it to tomorrow morning.' }]); };
  const handleReminderTime = (time) => {
    const ct = (reply.remindConfirm || "Reminder set for {time}. Moving on —").replace('{time}', time.toLowerCase());
    setReminderConvo(prev => [...prev, { role: 'user', text: time }, { role: 'agent', text: ct }]);
    setActionTaken('reminded'); onEmailHandled?.(emailId, 'reminded'); addTimer(() => setShowNextPrompt(true), 1500);
  };
  const handleNextEmail = () => { const next = pendingEmails.filter(e => e.id !== emailId); if (next.length > 0) onBack?.('next', next[0].id); else onBack?.('inbox-clear'); };

  // Handle edit request from chat input
  const handleEditRequest = (text) => {
    if (!text.trim() || actionTaken || !reply?.editScenario) return;
    // Hide original prompt/buttons
    setPromptVisible(false);
    // Add user message
    setEditMessages([{ role: 'user', text }]);
    // Show thinking
    setEditThinking(true);
    const editSec = reply.editScenario.thinkingTime || 4;
    addTimer(() => {
      setEditThinking(false);
      setEditDone(true);
      // Show Luc's response
      setEditMessages(prev => [...prev, { role: 'agent', text: reply.editScenario.lucResponse }]);
      // Show revised draft
      addTimer(() => {
        setEditDraftVisible(true);
        // Show action buttons again
        addTimer(() => setEditPromptVisible(true), 800);
      }, 1000);
    }, editSec * 400);
  };

  if (!emailData || !reply) return null;
  const thinkingSteps = reply.thinkingSteps || [];
  const currentStep = thinkingStep >= 0 && thinkingStep < thinkingSteps.length ? thinkingSteps[thinkingStep] : null;

  // Show action buttons: either original prompt or after edit
  const showActionButtons = (promptVisible && !actionTaken && editMessages.length === 0) || (editPromptVisible && !actionTaken);

  return (
    <div className="flex flex-col h-full" data-testid="email-reply-view">
      <style>{`@keyframes emailFade { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }`}</style>

      {/* Header */}
      <div className={`flex items-center gap-3 px-5 py-3.5 border-b ${divider} flex-shrink-0`}>
        <button onClick={() => onBack?.('back')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${isDark ? 'hover:bg-white/[0.06] text-white/60 hover:text-white' : 'hover:bg-black/[0.04] text-slate-500 hover:text-slate-800'}`} data-testid="email-reply-back"><ArrowLeft className="w-3.5 h-3.5" /> Back to Briefing</button>
        <div className={`h-4 w-px ${divider}`} />
        <img src="/Thumbnails/Luc.png" alt="" className="w-6 h-6 rounded-full object-cover" />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${textPrimary}`}>Reply to {reply.recipient}</p>
          <p className={`text-[10px] ${textSecondary}`}>{reply.subject}</p>
        </div>
        {actionTaken === 'sent' && <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium bg-emerald-500/15 text-emerald-400"><Check className="w-3.5 h-3.5" /> Sent</span>}
        {actionTaken === 'drafted' && <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium bg-indigo-500/15 text-indigo-400"><BookmarkPlus className="w-3.5 h-3.5" /> Drafted</span>}
        {actionTaken === 'reminded' && <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium bg-amber-500/15 text-amber-400"><Bell className="w-3.5 h-3.5" /> Reminder Set</span>}
      </div>

      <div className="flex-1 flex min-h-0">
        {/* Left: Email Context */}
        <div className={`w-[340px] flex-shrink-0 border-r ${divider} p-5 overflow-y-auto`}>
          <div className={`${cardBg} border ${divider} rounded-xl p-5 space-y-4`}>
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-bold ${isDark ? 'bg-white/[0.08] text-white/70' : 'bg-black/[0.06] text-slate-600'}`}>{emailData.senderInitials}</div>
              <div>
                <p className={`text-[13px] font-medium ${textPrimary}`}>{emailData.sender}</p>
                <p className={`text-[11px] ${textSecondary}`}>{emailData.time}</p>
              </div>
            </div>

            <p className={`text-[12px] font-medium leading-relaxed ${textPrimary}`}>{emailData.summary}</p>
            <p className={`text-[12px] leading-[1.7] ${isDark ? 'text-white/60' : 'text-slate-600'}`}>{emailData.expandedSummary}</p>

            {emailData.attachments?.length > 0 && (
              <div className={`pt-4 border-t ${divider}`}>
                <p className={`text-[11px] font-medium mb-2.5 ${textSecondary}`}>Attachments</p>
                <div className="space-y-2">
                  {emailData.attachments.map((a, i) => <AttachmentChip key={i} a={a} isDark={isDark} />)}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: Chat + bottom input */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Scrollable conversation */}
          <div className="flex-1 overflow-y-auto px-10 py-8">

            {/* Email from sender */}
            {emailShown && (
              <div style={{ animation: 'emailFade 0.4s ease-out' }}>
                <div className="flex items-start gap-4">
                  <img src="/Thumbnails/Luc.png" alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5" />
                  <div className={agentText}>
                    <p className={`font-semibold mb-2 ${textPrimary}`}>Email from {emailData.sender}</p>
                    <p>{emailData.summary}</p>
                    <button
                      onClick={() => setFullEmailOpen(!fullEmailOpen)}
                      className={`flex items-center gap-1.5 mt-4 text-[12px] font-medium ${isDark ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-500'} transition-colors`}
                      data-testid="read-full-email"
                    >
                      {fullEmailOpen ? 'Show less' : 'Read full email'}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${fullEmailOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {fullEmailOpen && (
                      <div className={`mt-4 pt-4 border-t ${divider} text-[13px] leading-[1.85] whitespace-pre-line`}>
                        {emailData.fullEmail || emailData.expandedSummary}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Multi-step thinking */}
            {currentStep && (
              <div className={`ml-[52px] rounded-xl px-5 py-4 ${isDark ? 'bg-indigo-500/8 border border-indigo-500/10' : 'bg-indigo-50 border border-indigo-200/40'}`} style={{ marginTop: 48, animation: 'emailFade 0.3s ease-out' }}>
                <div className="space-y-3.5">
                  {thinkingSteps.map((step, i) => {
                    const isActive = i === thinkingStep;
                    const isDoneStep = i < thinkingStep;
                    const isPending = i > thinkingStep;
                    const StepIcon = i === 0 ? Brain : i === 1 ? Search : PenLine;
                    return (
                      <div key={i} className={`flex items-center gap-3 transition-opacity duration-300 ${isPending ? 'opacity-20' : ''}`}>
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${isDoneStep ? (isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-500/15 text-emerald-600') : isActive ? (isDark ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-500/15 text-indigo-600') : (isDark ? 'bg-white/[0.06] text-white/20' : 'bg-black/[0.04] text-slate-400')}`}>
                          {isDoneStep ? <Check className="w-3 h-3" /> : isActive ? <StepIcon className="w-3 h-3 animate-pulse" /> : <span className="w-1 h-1 rounded-full bg-current" />}
                        </div>
                        <div>
                          <span className={`text-[12px] font-medium ${isActive ? (isDark ? 'text-indigo-400' : 'text-indigo-600') : isDoneStep ? (isDark ? 'text-white/60' : 'text-slate-600') : ''}`}>{step.label}</span>
                          {(isActive || isDoneStep) && <span className={`text-[11px] ml-2 ${isDark ? 'text-white/30' : 'text-slate-400'}`}>{step.detail}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Thinking time badge */}
            {thinkingDone && !currentStep && (
              <div className="ml-[52px] flex items-center gap-2" style={{ marginTop: 16, animation: 'emailFade 0.3s ease-out' }}>
                <Clock className={`w-3.5 h-3.5 ${isDark ? 'text-white/30' : 'text-slate-400'}`} />
                <span className={`text-[11px] ${isDark ? 'text-white/30' : 'text-slate-400'}`}>Thought for {thinkingSeconds}s</span>
              </div>
            )}

            {/* Luc's intro */}
            {introVisible && (
              <div className="flex items-start gap-4" style={{ marginTop: 48, animation: 'emailFade 0.4s ease-out' }}>
                <img src="/Thumbnails/Luc.png" alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5" />
                <div className={agentText}>
                  {reply.intro.split('\n').filter(Boolean).map((p, i, arr) => <p key={i} style={{ marginBottom: i < arr.length - 1 ? '20px' : 0 }}>{p}</p>)}
                </div>
              </div>
            )}

            {/* Draft + prompt as one continuous block */}
            {draftVisible && (
              <div className="ml-[52px]" style={{ marginTop: 48, animation: 'emailFade 0.4s ease-out' }}>
                <div className={`px-7 py-6 rounded-xl border ${isDark ? 'bg-white/[0.02] border-white/[0.06]' : 'bg-white/70 border-black/[0.05]'}`}>
                  <RichText text={editDraftVisible ? reply.editScenario?.revisedDraft || reply.draft : reply.draft} className={`text-[14px] leading-[1.8] ${isDark ? 'text-white/80' : 'text-slate-700'}`} />
                  {reply.attachments?.length > 0 && (
                    <div className={`mt-5 pt-5 border-t ${divider}`}>
                      <p className={`text-[11px] font-medium mb-3 flex items-center gap-1.5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>
                        <Paperclip className="w-3.5 h-3.5" /> Attachments
                      </p>
                      <div className="flex gap-3">
                        {reply.attachments.map((a, i) => <AttachmentChip key={i} a={a} isDark={isDark} compact />)}
                      </div>
                    </div>
                  )}
                </div>
                {/* Draft updated badge */}
                {editDraftVisible && (
                  <div className="flex items-center gap-1.5 mt-3" style={{ animation: 'emailFade 0.3s ease-out' }}>
                    <Check className={`w-3.5 h-3.5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                    <span className={`text-[11px] font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>Draft updated</span>
                  </div>
                )}
                {/* Original prompt — hidden once edit starts */}
                {promptVisible && !actionTaken && editMessages.length === 0 && (
                  <div style={{ marginTop: 32, animation: 'emailFade 0.3s ease-out' }}>
                    <p className={`text-[14px] leading-[1.8] ${isDark ? 'text-white/85' : 'text-slate-700'}`}>{reply.draftPrompt}</p>
                    <div className="flex items-center gap-2.5" style={{ marginTop: 20 }}>
                      <button onClick={handleSend} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-medium bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:opacity-90 transition-opacity" data-testid="email-send"><Send className="w-3.5 h-3.5" /> Send Now</button>
                      <button onClick={handleDraft} className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-medium border ${divider} ${textSecondary} ${isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]'} transition-colors`} data-testid="email-draft"><BookmarkPlus className="w-3.5 h-3.5" /> Save as Draft</button>
                      <button onClick={handleRemindLater} className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-medium border ${divider} ${textSecondary} ${isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]'} transition-colors`} data-testid="email-remind"><Clock className="w-3.5 h-3.5" /> Remind Later</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Edit conversation messages */}
            {editMessages.map((msg, i) => (
              <div key={i} style={{ marginTop: 48, animation: 'emailFade 0.35s ease-out' }}>
                {msg.role === 'user' ? (
                  <div className="flex justify-end"><div className={userBubble}>{msg.text}</div></div>
                ) : (
                  <div className="flex items-start gap-4">
                    <img src="/Thumbnails/Luc.png" alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5" />
                    <div className={agentText}>{msg.text}</div>
                  </div>
                )}
              </div>
            ))}

            {/* Edit thinking indicator */}
            {editThinking && (
              <div className="ml-[52px] flex items-center gap-2" style={{ marginTop: 24, animation: 'emailFade 0.3s ease-out' }}>
                <Loader2 className={`w-3.5 h-3.5 animate-spin ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
                <span className={`text-[11px] ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>Revising draft...</span>
              </div>
            )}

            {/* Post-edit action buttons */}
            {editPromptVisible && !actionTaken && (
              <div className="ml-[52px] flex items-center gap-2.5" style={{ marginTop: 24, animation: 'emailFade 0.3s ease-out' }}>
                <button onClick={handleSend} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-medium bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:opacity-90 transition-opacity" data-testid="email-send-revised"><Send className="w-3.5 h-3.5" /> Send Now</button>
                <button onClick={handleDraft} className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-medium border ${divider} ${textSecondary} ${isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]'} transition-colors`} data-testid="email-draft-revised"><BookmarkPlus className="w-3.5 h-3.5" /> Save as Draft</button>
                <button onClick={handleRemindLater} className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-medium border ${divider} ${textSecondary} ${isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]'} transition-colors`} data-testid="email-remind-revised"><Clock className="w-3.5 h-3.5" /> Remind Later</button>
              </div>
            )}

            {/* Confirmation */}
            {confirmVisible && (actionTaken === 'sent' || actionTaken === 'drafted') && (
              <div className="flex items-start gap-4" style={{ marginTop: 48, animation: 'emailFade 0.4s ease-out' }}>
                <img src="/Thumbnails/Luc.png" alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5" />
                <div className={agentText}>{actionTaken === 'sent' ? reply.sentConfirm : reply.draftConfirm}</div>
              </div>
            )}

            {/* Remind Later conversation */}
            {reminderConvo.map((msg, i) => (
              <div key={i} style={{ marginTop: 48, animation: 'emailFade 0.35s ease-out' }}>
                {msg.role === 'agent' ? (
                  <div className="flex items-start gap-4">
                    <img src="/Thumbnails/Luc.png" alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5" />
                    <div className={agentText}>{msg.text}</div>
                  </div>
                ) : (
                  <div className="flex justify-end"><div className={userBubble}>{msg.text}</div></div>
                )}
              </div>
            ))}

            {actionTaken === 'reminding' && reminderConvo.length === 1 && (
              <div className="flex flex-wrap gap-2.5 ml-[52px]" style={{ marginTop: 24, animation: 'emailFade 0.3s ease-out' }}>
                {['After lunch (1 PM)', 'End of day (5 PM)', 'Tomorrow morning'].map((opt) => (
                  <button key={opt} onClick={() => handleReminderTime(opt)} className={`px-4 py-2 rounded-xl text-[12px] font-medium border ${divider} ${textSecondary} ${isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]'} transition-colors`}>{opt}</button>
                ))}
              </div>
            )}

            {showNextPrompt && remaining > 0 && (
              <div className="flex items-center gap-2.5 ml-[52px]" style={{ marginTop: 24, animation: 'emailFade 0.3s ease-out' }}>
                <button onClick={handleNextEmail} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-medium bg-gradient-to-r from-indigo-500 to-purple-600 text-white" data-testid="email-next"><ChevronRight className="w-3.5 h-3.5" /> Next Email</button>
                <button onClick={() => onBack?.('back')} className={`px-4 py-2 rounded-xl text-[12px] font-medium border ${divider} ${textSecondary}`} data-testid="email-back-to-inbox">Back to Inbox</button>
              </div>
            )}

            {showNextPrompt && remaining === 0 && (
              <div style={{ marginTop: 48, animation: 'emailFade 0.4s ease-out' }}>
                <div className="flex items-start gap-4">
                  <img src="/Thumbnails/Luc.png" alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5" />
                  <div className={agentText}>
                    <p className={`font-semibold mb-2 ${textPrimary}`}>Priority inbox: cleared.</p>
                    <p>Nice work. You still have <strong>125 unread emails</strong> sitting in the full inbox though — I've been quietly scanning them. Most are newsletters, automated reports, and CC'd threads that don't need your attention.</p>
                    <p className="mt-3">Want me to sort them into "actually worth reading" vs "safe to archive"? I can also flag anything that needs a response by EOD — there might be 2-3 buried in there.</p>
                  </div>
                </div>
                <div className="flex items-center gap-2.5 mt-5 ml-[52px]">
                  <button className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-medium bg-gradient-to-r from-indigo-500 to-purple-600 text-white" data-testid="email-categorize">Yes, sort them for me</button>
                  <button onClick={() => onBack?.('back')} className={`px-4 py-2 rounded-xl text-[12px] font-medium border ${divider} ${textSecondary}`} data-testid="email-done">I'm good for now</button>
                </div>
              </div>
            )}

            <div ref={scrollRef} />
          </div>

          {/* Chat input bar — always visible */}
          <ChatInputBar
            isDark={isDark}
            onSend={handleEditRequest}
            disabled={!!actionTaken || editThinking || editMessages.length > 0}
            solidBg={solidBg}
            divider={divider}
            textPrimary={textPrimary}
            textSecondary={textSecondary}
            hoverBg={hoverBg}
          />
        </div>
      </div>
    </div>
  );
};

const ChatInputBar = ({ isDark, onSend, disabled, solidBg, divider, textPrimary, textSecondary, hoverBg }) => {
  const [message, setMessage] = useState('');
  const inputRef = useRef(null);

  const handleSend = () => {
    if (!message.trim() || disabled) return;
    onSend(message.trim());
    setMessage('');
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
  };

  const handleInput = (e) => {
    setMessage(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
  };

  return (
    <div className="flex justify-center pb-4 px-4 pt-2 flex-shrink-0">
      <div className="w-[85%]">
        <div className={`${solidBg} border ${divider} rounded-2xl flex flex-col`}>
          <div className="px-4 pt-3 pb-2">
            <textarea
              ref={inputRef}
              value={message}
              onChange={handleInput}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Message Luc about this email..."
              rows={1}
              disabled={disabled}
              className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${textPrimary} placeholder:${textSecondary} ${disabled ? 'opacity-40' : ''}`}
              style={{ height: 'auto', maxHeight: '200px', overflow: message.split('\n').length > 8 ? 'auto' : 'hidden' }}
              data-testid="email-chat-input"
            />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <button className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.08] ring-1 ring-indigo-500/40' : 'bg-indigo-50 ring-1 ring-indigo-300/60'}`}>
                <img src="/Thumbnails/Luc.png" alt="" className="w-5 h-5 rounded-full object-cover" />
                <span className={`text-xs font-medium ${textPrimary}`}>Luc</span>
              </button>
              <button className={`p-1.5 rounded-lg transition-colors ${hoverBg}`}>
                <Paperclip className={`w-3.5 h-3.5 ${textSecondary}`} />
              </button>
            </div>
            <div className="flex items-center gap-1.5">
              <button className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${hoverBg}`}>
                <Mic className={`w-4 h-4 ${textSecondary}`} />
              </button>
              <button
                onClick={handleSend}
                disabled={disabled || !message.trim()}
                className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${message.trim() && !disabled ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`}
                data-testid="email-chat-send"
              >
                <Send className={`w-3.5 h-3.5 ${message.trim() && !disabled ? 'text-white' : textSecondary}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default V4EmailReply;
